// Sous-navigation Espace Pro - Navigation 3 niveaux
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, FileText, Crown, Wand2, CreditCard, 
  Rocket, Users, Briefcase, Store, ChevronDown
} from 'lucide-react';
import { ESPACE_PRO_SECTIONS, getSectionsForRoute } from '../../config/espaceProSections';

const ESPACE_PRO_NAV_ITEMS = [
  { path: '/', label: 'Accueil', icon: Home, isExternal: true },
  { path: '/pro', label: 'Présentation', icon: FileText },
  { path: '/pro/boutique', label: 'Boutique', icon: Store },
  { path: '/pro/crm-batiment', label: 'Cockpit Bâtiment', icon: Briefcase },
  { path: '/pro/equipe-agents-ia', label: 'Équipe Agents IA', icon: Users },
  { path: '/pro/tarifs', label: 'Tarifs', icon: CreditCard },
  { path: '/pro/fondateur', label: 'Membre Fondateur', icon: Crown },
];

export default function EspaceProNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [isSubmenuHidden, setIsSubmenuHidden] = useState(true);
  const [hasScrolledPastHero, setHasScrolledPastHero] = useState(false);
  
  // Détecter si on est sur la page À propos (TopBanner masqué, pas de spacer)
  const isAboutPage = location.pathname === '/a-propos';
  // Position du menu : 72px pour plus d'espace avec le header
  const navTopPosition = '72px';
  
  const pageConfig = getSectionsForRoute(location.pathname);
  const currentSections = pageConfig?.sections || [];
  
  const isActive = (path) => {
    if (path === '/pro') {
      return location.pathname === '/pro';
    }
    return location.pathname.startsWith(path) && path !== '/';
  };
  
  useEffect(() => {
    setIsSubmenuHidden(true);
    setHasScrolledPastHero(false);
  }, [location.pathname]);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroThreshold = 500;
      
      if (scrollY > heroThreshold && !hasScrolledPastHero) {
        setHasScrolledPastHero(true);
        if (currentSections.length > 0) {
          setIsSubmenuHidden(false);
        }
      }
      if (scrollY < heroThreshold - 100 && hasScrolledPastHero) {
        setHasScrolledPastHero(false);
        setIsSubmenuHidden(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolledPastHero, currentSections]);
  
  useEffect(() => {
    if (!currentSections.length) return;
    
    const handleScroll = () => {
      for (let i = currentSections.length - 1; i >= 0; i--) {
        const el = document.getElementById(currentSections[i].id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200) {
            setActiveSection(currentSections[i].id);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentSections, location.pathname]);
  
  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      const offset = 180;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };
  
  const handleNavClick = (e, item) => {
    const active = isActive(item.path);
    const hasSections = ESPACE_PRO_SECTIONS[item.path]?.sections;
    
    if (active && hasSections) {
      e.preventDefault();
      setIsSubmenuHidden(!isSubmenuHidden);
    }
  };

  return (
    <>
      {/* MENU PRINCIPAL - FOND BLANC */}
      <nav 
        className="bg-white border-b border-gray-200 w-full hidden lg:block espacepr-main-nav"
        style={{ 
          position: 'fixed',
          top: navTopPosition,
          left: 0,
          right: 0,
          zIndex: 98,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <div 
          className="flex items-center justify-center px-4 gap-2"
          style={{ 
            height: '58px',
          }}
        >
          {ESPACE_PRO_NAV_ITEMS.map((item) => {
            const active = isActive(item.path);
            const hasSections = ESPACE_PRO_SECTIONS[item.path]?.sections;
            const Icon = item.icon;
            const showSubmenuIndicator = active && hasSections && !isSubmenuHidden;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(e, item)}
                className={`espacepr-nav-link flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-[#1E3A5F] text-white'
                    : 'text-[#1E3A5F] hover:bg-gray-100'
                }`}
              >
                <Icon size={18} className={active ? 'text-[#F97316]' : 'text-gray-400'} />
                <span>{item.label}</span>
                {hasSections && (
                  <ChevronDown 
                    size={14} 
                    className={`transition-transform duration-200 ${showSubmenuIndicator ? 'rotate-180' : ''} ${active ? 'text-white/70' : 'text-gray-400'}`} 
                  />
                )}
              </Link>
            );
          })}
        </div>
      </nav>
      
      {/* SOUS-MENU LANDING (ORANGE DOUX) - Apparaît au scroll */}
      <AnimatePresence>
        {hasScrolledPastHero && currentSections.length > 0 && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="w-full hidden lg:block"
            style={{ 
              position: 'fixed',
              top: '130px', // Header 72px + Menu 58px
              left: 0,
              right: 0,
              zIndex: 50,
              background: '#FFF8F4',
              borderTop: '1px solid rgba(249, 115, 22, 0.08)',
              borderBottom: '1px solid rgba(249, 115, 22, 0.10)',
              boxShadow: '0 6px 18px rgba(30, 58, 95, 0.05)',
            }}
          >
            <div 
              className="flex items-center justify-center py-3 px-4 gap-2"
              style={{
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {currentSections.map((section) => {
                const SectionIcon = section.icon;
                const isActiveSection = activeSection === section.id;
                
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap"
                    style={{
                      background: isActiveSection ? '#F97316' : '#FFFFFF',
                      color: isActiveSection ? '#FFFFFF' : '#1E3A5F',
                      border: isActiveSection ? 'none' : '1px solid rgba(30, 58, 95, 0.12)',
                      boxShadow: isActiveSection ? '0 4px 12px rgba(249, 115, 22, 0.25)' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActiveSection) {
                        e.currentTarget.style.background = 'rgba(249, 115, 22, 0.08)';
                        e.currentTarget.style.borderColor = '#F97316';
                        e.currentTarget.style.color = '#F97316';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActiveSection) {
                        e.currentTarget.style.background = '#FFFFFF';
                        e.currentTarget.style.borderColor = 'rgba(30, 58, 95, 0.12)';
                        e.currentTarget.style.color = '#1E3A5F';
                      }
                    }}
                  >
                    <SectionIcon 
                      size={16} 
                      style={{ color: isActiveSection ? '#FFFFFF' : '#F97316' }} 
                    />
                    <span>{section.label}</span>
                  </button>
                );
              })}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      
      {/* Spacer supprimé - le hero gère son propre espacement */}
    </>
  );
}

export { ESPACE_PRO_NAV_ITEMS };
